import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Palette, Save, RotateCcw, Upload, Trash2 } from "lucide-react";
import {
  SITES,
  SITE_LABEL,
  TAG_PRESETS,
  TAG_PRESET_KEYS,
  DEFAULT_SITE_TAG_PRESETS,
  DEPARTMENTS,
  DEFAULT_DEPT_TAG_PRESETS,
  STATUSES,
  DEFAULT_STATUS_TAG_PRESETS,
  PRIORITIES,
  DEFAULT_PRIORITY_TAG_PRESETS,
  setSiteTagMap,
  setDeptTagMap,
  setStatusTagMap,
  setPriorityTagMap,
  setSiteCrests,
  setSiteCrest,
} from "@/lib/sites";

export default function TagSettings() {
  const [map, setMap] = useState({ ...DEFAULT_SITE_TAG_PRESETS });
  const [deptMap, setDeptMap] = useState({ ...DEFAULT_DEPT_TAG_PRESETS });
  const [statusMap, setStatusMap] = useState({ ...DEFAULT_STATUS_TAG_PRESETS });
  const [priorityMap, setPriorityMap] = useState({ ...DEFAULT_PRIORITY_TAG_PRESETS });
  const [crests, setCrests] = useState({});
  const [saving, setSaving] = useState(false);
  const [deptSaving, setDeptSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [prioritySaving, setPrioritySaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busySite, setBusySite] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const [colorsRes, deptRes, statusRes, priorityRes, crestsRes] = await Promise.all([
          api.get("/settings/site-tags"),
          api.get("/settings/dept-tags"),
          api.get("/settings/status-tags"),
          api.get("/settings/priority-tags"),
          api.get("/settings/site-crests"),
        ]);
        setMap({ ...DEFAULT_SITE_TAG_PRESETS, ...colorsRes.data });
        setDeptMap({ ...DEFAULT_DEPT_TAG_PRESETS, ...deptRes.data });
        setStatusMap({ ...DEFAULT_STATUS_TAG_PRESETS, ...statusRes.data });
        setPriorityMap({ ...DEFAULT_PRIORITY_TAG_PRESETS, ...priorityRes.data });
        setCrests(crestsRes.data || {});
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pick = (code, preset) => setMap((m) => ({ ...m, [code]: preset }));
  const pickDept = (dept, preset) => setDeptMap((m) => ({ ...m, [dept]: preset }));
  const pickStatus = (status, preset) => setStatusMap((m) => ({ ...m, [status]: preset }));
  const pickPriority = (priority, preset) => setPriorityMap((m) => ({ ...m, [priority]: preset }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/site-tags", { colors: map });
      setSiteTagMap(data);
      toast.success("Site tag colors saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveDept = async () => {
    setDeptSaving(true);
    try {
      const { data } = await api.put("/settings/dept-tags", { colors: deptMap });
      setDeptTagMap(data);
      toast.success("Department tag colors saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeptSaving(false);
    }
  };

  const saveStatus = async () => {
    setStatusSaving(true);
    try {
      const { data } = await api.put("/settings/status-tags", { colors: statusMap });
      setStatusTagMap(data);
      toast.success("Status tag colors saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStatusSaving(false);
    }
  };

  const savePriority = async () => {
    setPrioritySaving(true);
    try {
      const { data } = await api.put("/settings/priority-tags", { colors: priorityMap });
      setPriorityTagMap(data);
      toast.success("Priority tag colors saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPrioritySaving(false);
    }
  };

  const resetDefaults = () => setMap({ ...DEFAULT_SITE_TAG_PRESETS });

  const uploadCrest = async (code, file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Image too large (max 1MB). Try a smaller crest.");
      return;
    }
    setBusySite(code);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data } = await api.put("/settings/site-crest", { site: code, data_url: dataUrl });
      setCrests(data);
      setSiteCrests(data);
      toast.success(`${code} crest updated`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusySite(null);
      if (fileRefs.current[code]) fileRefs.current[code].value = "";
    }
  };

  const removeCrest = async (code) => {
    if (!confirm(`Remove ${code} crest?`)) return;
    setBusySite(code);
    try {
      const { data } = await api.put("/settings/site-crest", { site: code, data_url: null });
      setCrests(data);
      setSiteCrest(code, null);
      toast.success(`${code} crest removed`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusySite(null);
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="tag-settings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
            <Palette className="h-3.5 w-3.5" /> Admin
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mt-1">Site tag colors</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Pick a soft palette for each site's pill on the ticket dashboard, and upload a school crest to
            show next to the pill. Changes save district-wide and take effect on next page load.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={resetDefaults}
            data-testid="tag-reset-defaults"
            className="h-11 rounded-md border-zinc-300 text-zinc-800 hover:bg-zinc-50 dark:border-zinc-600 dark:text-white dark:hover:bg-zinc-800 dark:hover:text-white"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reset defaults
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            data-testid="tag-save-button"
            className="h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white"
          >
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SITES.map((s) => {
          const currentKey = map[s.code] || DEFAULT_SITE_TAG_PRESETS[s.code] || "zinc";
          const currentClasses = TAG_PRESETS[currentKey]?.classes || TAG_PRESETS.zinc.classes;
          return (
            <div
              key={s.code}
              className="bg-white border border-zinc-200 rounded-md p-5"
              data-testid={`tag-site-card-${s.code}`}
            >
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">{s.code}</div>
                  <div className="font-heading font-semibold text-zinc-950 truncate">{SITE_LABEL[s.code]}</div>
                </div>
                <div className="inline-flex items-center gap-1.5 shrink-0" data-testid={`tag-preview-${s.code}`}>
                  {crests[s.code] && (
                    <img
                      src={crests[s.code]}
                      alt=""
                      className="h-5 w-5 rounded-sm object-contain"
                      data-testid={`tag-preview-crest-${s.code}`}
                    />
                  )}
                  <span className={`inline-flex items-center px-3 py-1 rounded-full font-mono text-xs border ${currentClasses}`}>
                    {s.short}
                  </span>
                </div>
              </div>

              {/* Crest upload row */}
              <div className="flex items-center gap-3 mb-4 rounded-md border border-dashed border-zinc-300 bg-zinc-50/60 p-3">
                <div className="h-10 w-10 rounded-md bg-white border border-zinc-200 grid place-items-center overflow-hidden shrink-0">
                  {crests[s.code] ? (
                    <img src={crests[s.code]} alt="" className="h-full w-full object-contain" data-testid={`crest-thumb-${s.code}`} />
                  ) : (
                    <span className="text-[9px] uppercase tracking-[0.1em] text-zinc-400">Crest</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-zinc-800">School crest</div>
                  <div className="text-[10px] text-zinc-500 leading-snug">PNG, JPG, SVG or WebP · under 1MB · shown next to the pill</div>
                </div>
                <input
                  ref={(el) => { fileRefs.current[s.code] = el; }}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                  className="hidden"
                  data-testid={`crest-file-input-${s.code}`}
                  onChange={(e) => uploadCrest(s.code, e.target.files?.[0])}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRefs.current[s.code]?.click()}
                  disabled={busySite === s.code}
                  data-testid={`crest-upload-${s.code}`}
                  className="h-8 text-zinc-800 dark:text-white dark:border-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-white"
                >
                  <Upload className="h-3.5 w-3.5 mr-1" /> {crests[s.code] ? "Replace" : "Upload"}
                </Button>
                {crests[s.code] && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCrest(s.code)}
                    disabled={busySite === s.code}
                    data-testid={`crest-remove-${s.code}`}
                    className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-8 gap-2">
                {TAG_PRESET_KEYS.map((k) => {
                  const p = TAG_PRESETS[k];
                  const isSelected = currentKey === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      title={p.label}
                      onClick={() => pick(s.code, k)}
                      data-testid={`tag-swatch-${s.code}-${k}`}
                      className={`h-9 rounded-md border-2 transition-all duration-150 ${
                        isSelected
                          ? "border-zinc-950 ring-2 ring-zinc-950/10 scale-105"
                          : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      style={{ backgroundColor: p.swatch }}
                      aria-label={`${s.code} ${p.label}`}
                    />
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.08em] text-zinc-500">
                {TAG_PRESETS[currentKey]?.label || currentKey}
              </div>
            </div>
          );
        })}
      </div>

      {/* -------- Department tag colors -------- */}
      <div className="mt-10 flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
            <Palette className="h-3.5 w-3.5" /> Departments
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 dark:text-white mt-1">Department tag colors</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1 max-w-2xl">
            The pill color used everywhere a department is shown — ticket rows, detail sheet, hotspot chart legend, and roster. Same 16 presets as sites.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setDeptMap({ ...DEFAULT_DEPT_TAG_PRESETS })}
            data-testid="dept-reset-defaults"
            className="h-11 rounded-md"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reset defaults
          </Button>
          <Button
            onClick={saveDept}
            disabled={deptSaving || loading}
            data-testid="dept-save-button"
            className="h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white"
          >
            <Save className="h-4 w-4 mr-2" /> {deptSaving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {DEPARTMENTS.map((d) => {
          const currentKey = deptMap[d] || DEFAULT_DEPT_TAG_PRESETS[d] || "zinc";
          const currentClasses = TAG_PRESETS[currentKey]?.classes || TAG_PRESETS.zinc.classes;
          return (
            <div key={d} className="bg-white border border-zinc-200 rounded-md p-5" data-testid={`tag-dept-card-${d}`}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Department</div>
                  <div className="font-heading font-semibold text-zinc-950 dark:text-white">{d}</div>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full font-mono text-xs border shrink-0 ${currentClasses}`}
                  data-testid={`dept-preview-${d}`}
                >
                  {d}
                </span>
              </div>
              <div className="grid grid-cols-8 gap-2">
                {TAG_PRESET_KEYS.map((k) => {
                  const p = TAG_PRESETS[k];
                  const isSelected = currentKey === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      title={p.label}
                      onClick={() => pickDept(d, k)}
                      data-testid={`dept-swatch-${d}-${k}`}
                      className={`h-9 rounded-md border-2 transition-all duration-150 ${
                        isSelected
                          ? "border-zinc-950 dark:border-white ring-2 ring-zinc-950/10 scale-105"
                          : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      style={{ backgroundColor: p.swatch }}
                      aria-label={`${d} ${p.label}`}
                    />
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.08em] text-zinc-500">
                {TAG_PRESETS[currentKey]?.label || currentKey}
              </div>
            </div>
          );
        })}
      </div>

      {/* -------- Status tag colors -------- */}
      <div className="mt-10 flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
            <Palette className="h-3.5 w-3.5" /> Statuses
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 dark:text-white mt-1">Status tag colors</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1 max-w-2xl">
            The pill color used for a ticket's <strong>status</strong> — Open, In Progress, Resolved, Closed. Same 16 presets as sites and departments.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setStatusMap({ ...DEFAULT_STATUS_TAG_PRESETS })}
            data-testid="status-reset-defaults"
            className="h-11 rounded-md"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reset defaults
          </Button>
          <Button
            onClick={saveStatus}
            disabled={statusSaving || loading}
            data-testid="status-save-button"
            className="h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white"
          >
            <Save className="h-4 w-4 mr-2" /> {statusSaving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {STATUSES.map((s) => {
          const currentKey = statusMap[s] || DEFAULT_STATUS_TAG_PRESETS[s] || "zinc";
          const currentClasses = TAG_PRESETS[currentKey]?.classes || TAG_PRESETS.zinc.classes;
          return (
            <div key={s} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-md p-5" data-testid={`tag-status-card-${s}`}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Status</div>
                  <div className="font-heading font-semibold text-zinc-950 dark:text-white">{s}</div>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium border shrink-0 ${currentClasses}`}
                  data-testid={`status-preview-${s}`}
                >
                  {s}
                </span>
              </div>
              <div className="grid grid-cols-8 gap-2">
                {TAG_PRESET_KEYS.map((k) => {
                  const p = TAG_PRESETS[k];
                  const isSelected = currentKey === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      title={p.label}
                      onClick={() => pickStatus(s, k)}
                      data-testid={`status-swatch-${s}-${k}`}
                      className={`h-9 rounded-md border-2 transition-all duration-150 ${
                        isSelected
                          ? "border-zinc-950 dark:border-white ring-2 ring-zinc-950/10 scale-105"
                          : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      style={{ backgroundColor: p.swatch }}
                      aria-label={`${s} ${p.label}`}
                    />
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.08em] text-zinc-500">
                {TAG_PRESETS[currentKey]?.label || currentKey}
              </div>
            </div>
          );
        })}
      </div>

      {/* -------- Priority tag colors -------- */}
      <div className="mt-10 flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
            <Palette className="h-3.5 w-3.5" /> Priorities
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 dark:text-white mt-1">Priority tag colors</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1 max-w-2xl">
            The pill color used for a ticket's <strong>priority</strong> — Low, Medium, High, Critical. Priority pills keep their signature neon backlit glow; each preset ships with a matching glow color.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPriorityMap({ ...DEFAULT_PRIORITY_TAG_PRESETS })}
            data-testid="priority-reset-defaults"
            className="h-11 rounded-md"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reset defaults
          </Button>
          <Button
            onClick={savePriority}
            disabled={prioritySaving || loading}
            data-testid="priority-save-button"
            className="h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white"
          >
            <Save className="h-4 w-4 mr-2" /> {prioritySaving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {PRIORITIES.map((p) => {
          const currentKey = priorityMap[p] || DEFAULT_PRIORITY_TAG_PRESETS[p] || "zinc";
          const preset = TAG_PRESETS[currentKey] || TAG_PRESETS.zinc;
          const currentClasses = `${preset.classes} ${preset.neon || ""}`;
          return (
            <div key={p} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-md p-5" data-testid={`tag-priority-card-${p}`}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Priority</div>
                  <div className="font-heading font-semibold text-zinc-950 dark:text-white">{p}</div>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium border shrink-0 ${currentClasses}`}
                  data-testid={`priority-preview-${p}`}
                >
                  {p}
                </span>
              </div>
              <div className="grid grid-cols-8 gap-2">
                {TAG_PRESET_KEYS.map((k) => {
                  const pp = TAG_PRESETS[k];
                  const isSelected = currentKey === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      title={pp.label}
                      onClick={() => pickPriority(p, k)}
                      data-testid={`priority-swatch-${p}-${k}`}
                      className={`h-9 rounded-md border-2 transition-all duration-150 ${
                        isSelected
                          ? "border-zinc-950 dark:border-white ring-2 ring-zinc-950/10 scale-105"
                          : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      style={{ backgroundColor: pp.swatch }}
                      aria-label={`${p} ${pp.label}`}
                    />
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.08em] text-zinc-500">
                {TAG_PRESETS[currentKey]?.label || currentKey}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
